import React from "react";
import { Box, VStack, Flex, HStack, Text, Heading } from "@chakra-ui/react";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";

type AdminShellProps = {
    title: string;
    subtitle?: string;
    badgeText?: string;
    children: React.ReactNode;
};

export const AdminShell: React.FC<AdminShellProps> = ({
    title,
    subtitle,
    badgeText,
    children,
}) => {
    const { theme } = useCustomTheme();
    const isDark = theme.name === "dark";

    const brand = theme.brand || {
        electricBlue: "#27b7ff",
        indigo: "#6f7cff",
        violet: "#b05cff",
    };

    const gradients = theme.gradients || {
        pageBg: theme.background,
        shellBg: theme.surface,
    };

    const pageBg = gradients.pageBg || theme.background;
    const shellBg = gradients.shellBg || theme.surface;
    const textColor = theme.text;
    const mutedText = theme.textMuted;
    const panelShadow = theme.shadowMd;
    const softShadow = theme.shadowSm;
    const cardsBorderRadii = "8px";

    return (
        <Box h="100%" minH="100%" w="100%" bg={pageBg}>
            <VStack h="100%" minH="100%" w="100%" spacing={0} align="stretch">
                <Box
                    flex="1"
                    minH={0}
                    w="100%"
                    borderRadius={{ base: cardsBorderRadii, md: cardsBorderRadii }}
                    bg={shellBg}
                    boxShadow={panelShadow}
                    p={{ base: 2, md: 3 }}
                    backdropFilter={isDark ? "blur(10px)" : undefined}
                >
                    <Flex
                        justify="space-between"
                        align={{ base: "flex-start", md: "center" }}
                        direction={{ base: "column", md: "row" }}
                        gap={3}
                        mb={3}
                    >
                        <Box>
                            <Heading size="lg" color={textColor}>
                                {title}
                            </Heading>
                            {subtitle && (
                                <Text color={mutedText} fontWeight="500" mt={1}>
                                    {subtitle}
                                </Text>
                            )}
                        </Box>

                        {badgeText && (
                            <HStack
                                px={3}
                                py={2}
                                borderRadius={cardsBorderRadii}
                                border={`1px solid ${theme.border}`}
                                bg={isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.8)"}
                                boxShadow={softShadow}
                                spacing={2}
                            >
                                <Box
                                    w="8px"
                                    h="8px"
                                    borderRadius={cardsBorderRadii}
                                    bg={brand.electricBlue}
                                />
                                <Text color={textColor} fontWeight="700" fontSize="sm">
                                    {badgeText}
                                </Text>
                            </HStack>
                        )}
                    </Flex>

                    {children}
                </Box>
            </VStack>
        </Box>
    );
};